import React, { useState } from 'react';
import { 
  ArrowLeft, Search, Star, Filter, ChevronDown, ChevronUp, 
  Clock, Package, Truck, DollarSign, ThumbsUp
} from 'lucide-react';
import { 
  suppliers, 
  supplierRatings, 
  purchaseOrders 
} from '../../data/purchaseOrderData';

const SupplierRatings = ({ onBack }) => {
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('overall_rating');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Get supplier details with ratings
  const getSuppliersWithRatings = () => {
    return suppliers.map(supplier => {
      // Get all ratings for this supplier
      const ratings = supplierRatings.filter(rating => rating.supplier_id === supplier.id);
      
      // Calculate average ratings
      const calculateAverage = (field) => {
        const fieldRatings = ratings.map(r => r[field]).filter(r => r !== undefined && r !== null);
        if (fieldRatings.length === 0) return 0;
        return fieldRatings.reduce((sum, rating) => sum + rating, 0) / fieldRatings.length;
      };
      
      // Get order count and total spend
      const supplierOrders = purchaseOrders.filter(po => po.supplier_id === supplier.id);
      const orderCount = supplierOrders.length;
      const totalSpend = supplierOrders.reduce((sum, po) => sum + po.total, 0);
      
      return {
        ...supplier,
        quality_rating: calculateAverage('quality_rating'),
        delivery_rating: calculateAverage('delivery_rating'),
        price_rating: calculateAverage('price_rating'),
        service_rating: calculateAverage('service_rating'),
        overall_rating: calculateAverage('overall_rating'),
        rating_count: ratings.length,
        order_count: orderCount,
        total_spend: totalSpend
      };
    });
  };
  
  // Get all suppliers with ratings
  const suppliersWithRatings = getSuppliersWithRatings();
  
  // Filter suppliers based on search term
  const filteredSuppliers = suppliersWithRatings.filter(supplier => {
    return (
      supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (supplier.contact_person && supplier.contact_person.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });
  
  // Sort suppliers
  const sortedSuppliers = [...filteredSuppliers].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // Handle string vs number comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? 
        aValue.localeCompare(bValue) : 
        bValue.localeCompare(aValue);
    } else {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
  });
  
  // Handle sort change
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc'); // Default to descending for new sort field
    }
  };
  
  // Render stars for rating
  const renderRatingStars = (rating) => {
    const roundedRating = Math.round(rating * 2) / 2; // Round to nearest 0.5
    const stars = [];
    
    for (let i = 1; i <= 5; i++) {
      if (i <= roundedRating) {
        // Full star
        stars.push(
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        );
      } else if (i - 0.5 === roundedRating) {
        // Half star (represented by a smaller star for simplicity)
        stars.push(
          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400 opacity-50" />
        );
      } else {
        // Empty star
        stars.push(
          <Star key={i} className="h-4 w-4 text-gray-300" />
        );
      }
    }
    
    return (
      <div className="flex items-center space-x-1">
        {stars}
        <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">供應商評分</h1>
        </div>
      </div>
      
      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Search */}
          <div className="relative flex-grow max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="搜尋供應商..."
            />
          </div>
          
          {/* Filter Button - For future expansion */}
          <div>
            <button className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2">
              <Filter className="h-4 w-4" />
              <span>篩選</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* Suppliers Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button 
                    onClick={() => handleSort('name')} 
                    className="flex items-center space-x-1 hover:text-gray-700"
                  >
                    <span>供應商</span>
                    {sortField === 'name' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('overall_rating')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <ThumbsUp className="h-4 w-4" />
                    <span>整體評分</span>
                    {sortField === 'overall_rating' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('quality_rating')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <Package className="h-4 w-4" />
                    <span>品質</span>
                    {sortField === 'quality_rating' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('delivery_rating')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <Truck className="h-4 w-4" />
                    <span>交期</span>
                    {sortField === 'delivery_rating' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('price_rating')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <DollarSign className="h-4 w-4" />
                    <span>價格</span>
                    {sortField === 'price_rating' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('service_rating')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <Clock className="h-4 w-4" />
                    <span>服務</span>
                    {sortField === 'service_rating' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('rating_count')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <span>評分數</span>
                    {sortField === 'rating_count' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-center">
                  <button 
                    onClick={() => handleSort('order_count')} 
                    className="flex items-center justify-center space-x-1 hover:text-gray-700 mx-auto"
                  >
                    <span>訂單數</span>
                    {sortField === 'order_count' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-right">
                  <button 
                    onClick={() => handleSort('total_spend')} 
                    className="flex items-center justify-end space-x-1 hover:text-gray-700 ml-auto"
                  >
                    <span>總支出</span>
                    {sortField === 'total_spend' && (
                      sortDirection === 'asc' ? 
                        <ChevronUp className="h-4 w-4" /> : 
                        <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white text-sm">
              {sortedSuppliers.map(supplier => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{supplier.name}</p>
                      {supplier.contact_person && (
                        <p className="text-gray-500">{supplier.contact_person}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {renderRatingStars(supplier.overall_rating)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {renderRatingStars(supplier.quality_rating)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {renderRatingStars(supplier.delivery_rating)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {renderRatingStars(supplier.price_rating)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-center">
                      {renderRatingStars(supplier.service_rating)}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {supplier.rating_count}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {supplier.order_count}
                  </td>
                  <td className="px-6 py-4 text-right font-medium">
                    ${supplier.total_spend.toLocaleString()}
                  </td>
                </tr>
              ))}
              
              {sortedSuppliers.length === 0 && (
                <tr>
                  <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                    找不到符合條件的供應商
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SupplierRatings;
